import * as React from 'react'
import { AccountDto } from '@halo/dtos/account.dto'
import { Button, Drawer, executeCleanDrawerRender, Well } from '@pushly/aqe/lib/components'
import { DeleteFilled, EditOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Button as AntButton, Input, InputNumber, notification, Select, Skeleton } from 'antd'
import { classnames } from '@halo/_utils/classnames'
import moment from 'moment-timezone'
import { simpleFormErrorNotification, simpleNotification } from '@halo/_utils/notifications'
import { FormComponentProps } from '@ant-design/compatible/lib/form'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { AccountService } from '@halo/services/account.service'
import { Container } from 'typescript-ioc/es5'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import { legacyValidateFields } from '@halo/_utils/antd'
import cloneDeep from 'clone-deep'
import CopyToClipboard from 'react-copy-to-clipboard'
import * as deepEqual from 'react-fast-compare'
import * as randomstring from 'randomstring'
// @ts-ignore
import { JsonEditor } from 'jsoneditor-react'
import 'jsoneditor-react/es/editor.min.css'
import { IAddedNotificationQueryParam } from '@halo/interfaces/added-notification-query-param'
import clone from 'clone-deep'
import TemplatizedTextField from '@halo/components/templatized-text-field/templatized-text-field'

function calculateRawTtlSecondsFromValue(value: number, metric: 'days' | 'hours' | 'minutes' | 'seconds'): number {
    let computed = value

    switch (metric) {
        case 'minutes':
            computed = computed * 60
            break
        case 'hours':
            computed = computed * 60 * 60
            break
        case 'days':
            computed = computed * 60 * 60 * 24
            break
    }

    return computed
}

function calculateValueFromRawTtlSeconds(value: number, metric: 'days' | 'hours' | 'minutes' | 'seconds'): number {
    let computed = value

    switch (metric) {
        case 'minutes':
            computed = computed / 60
            break
        case 'hours':
            computed = computed / 60 / 60
            break
        case 'days':
            computed = computed / 24 / 60 / 60
            break
    }

    return computed
}

interface IAccountNotificationEditor extends FormComponentProps {
    account: AccountDto
    visible: boolean
    onClose: (e: any) => void
    onAccountUpdated: (account: Partial<AccountDto>) => void
}

interface IANEState {
    customUtmParams: Array<[string, IAddedNotificationQueryParam]>
}

export const AccountNotificationEditor = Form.create<IAccountNotificationEditor>()(
    class extends React.Component<IAccountNotificationEditor, IANEState> {
        public state: IANEState = {
            customUtmParams: [],
        }

        protected appState: AppState
        protected appService: AppService
        protected accountService: AccountService

        public constructor(props: IAccountNotificationEditor) {
            super(props)

            this.appState = Container.get(AppState)
            this.appService = Container.get(AppService)
            this.accountService = Container.get(AccountService)

            executeCleanDrawerRender(this)
        }

        public async componentDidMount() {
            const customUtmParams = Object.entries(clone(this.props.account.added_notification_query_params ?? {}))

            this.setState({
                customUtmParams,
            })
        }

        public render() {
            const currentUserIsDev = this.appState.currentUser?.isInternalDevUser || false
            const currentValues = this.getCurrentValues()

            const rawDefaultTtlSeconds = this.account.default_notification_ttl_seconds
            const defaultTtlMetric = this.account.display_meta?.default_notification_ttl_metric || 'days'
            const defaultTtlValue = calculateValueFromRawTtlSeconds(rawDefaultTtlSeconds, defaultTtlMetric)

            const utmMacroTypes = ['domain', 'location', 'notification', 'custom']

            return (
                <Drawer
                    className={classnames('adf-notif-settings-editor')}
                    title="Update Notification Settings"
                    placement="right"
                    closable={true}
                    onSubmit={this.handleSubmit}
                    disableSubmit={Object.keys(currentValues).length === 0}
                    onClose={this.props.onClose}
                    visible={this.props.visible}
                >
                    <Form {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}>
                        <Well title="Keywords" mode="ghost" hideFooter={true}>
                            <Form.Item label="Default Keywords" className={classnames('adf-notif-editor-keywords')}>
                                {this.props.form.getFieldDecorator('keywords', {
                                    initialValue: (this.account.keywords || []).join('\n'),
                                })(<Input.TextArea />)}
                                <div className={classnames('form-layout-row-sub')}>
                                    <InfoCircleOutlined />
                                    <span>Keywords must be entered one per line.</span>
                                </div>
                            </Form.Item>
                        </Well>

                        <Well title="Delivery" mode="ghost" hideFooter={true}>
                            <Form.Item label="Default Lifespan" className={classnames('adf-notif-editor-lifespan')}>
                                {this.props.form.getFieldDecorator('ttlValue', {
                                    initialValue: defaultTtlValue,
                                })(
                                    <Input
                                        autoComplete="off"
                                        addonAfter={this.props.form.getFieldDecorator('ttlMetric', {
                                            initialValue: defaultTtlMetric,
                                        })(
                                            <Select>
                                                <Select.Option value="minutes">minute(s)</Select.Option>
                                                <Select.Option value="hours">hour(s)</Select.Option>
                                                <Select.Option value="days">day(s)</Select.Option>
                                            </Select>,
                                        )}
                                    />,
                                )}
                            </Form.Item>
                        </Well>

                        <Well title="Media" mode="ghost" hideFooter={true}>
                            <Form.Item label="Default Quality" className={classnames('adf-notif-editor-media-quality')}>
                                {this.props.form.getFieldDecorator('mediaQuality', {
                                    initialValue: this.account.media_quality || 40,
                                })(<InputNumber size="small" min={30} max={100} />)}
                            </Form.Item>
                        </Well>

                        <Well title="UTM Parameters" mode="ghost" hideFooter={true}>
                            <div className={classnames('adf-notif-editor-utm-source')}>
                                {this.props.form.getFieldDecorator('utmSource', {
                                    initialValue: !this.account.utm_params?.utm_source
                                        ? undefined
                                        : this.account.utm_params.utm_source,
                                    rules: [
                                        {
                                            required: true,
                                            message: 'utm_source is required.',
                                        },
                                    ],
                                })(
                                    <TemplatizedTextField
                                        label="utm_source"
                                        placeholder="utm source"
                                        hideEmojis={true}
                                        macroTypes={utmMacroTypes}
                                        size="small"
                                    />,
                                )}
                            </div>
                            <div className={classnames('adf-notif-editor-utm-medium')}>
                                {this.props.form.getFieldDecorator('utmMedium', {
                                    initialValue: !this.account.utm_params?.utm_medium
                                        ? undefined
                                        : this.account.utm_params.utm_medium,
                                })(
                                    <TemplatizedTextField
                                        label="utm_medium"
                                        placeholder="utm medium"
                                        hideEmojis={true}
                                        macroTypes={utmMacroTypes}
                                        size="small"
                                    />,
                                )}
                            </div>
                            <div className={classnames('adf-notif-editor-utm-campaign')}>
                                {this.props.form.getFieldDecorator('utmCampaign', {
                                    initialValue: !this.account.utm_params?.utm_campaign
                                        ? undefined
                                        : this.account.utm_params.utm_campaign,
                                })(
                                    <TemplatizedTextField
                                        label="utm_campaign"
                                        placeholder="utm campaign"
                                        hideEmojis={true}
                                        macroTypes={utmMacroTypes}
                                        size="small"
                                    />,
                                )}
                            </div>
                            <div className={classnames('adf-notif-editor-utm-term')}>
                                {this.props.form.getFieldDecorator('utmTerm', {
                                    initialValue: !this.account.utm_params?.utm_term
                                        ? undefined
                                        : this.account.utm_params.utm_term,
                                })(
                                    <TemplatizedTextField
                                        label="utm_term"
                                        placeholder="utm term"
                                        hideEmojis={true}
                                        macroTypes={utmMacroTypes}
                                        size="small"
                                    />,
                                )}
                            </div>
                            <div className={classnames('adf-notif-editor-utm-content')}>
                                {this.props.form.getFieldDecorator('utmContent', {
                                    initialValue: !this.account.utm_params?.utm_content
                                        ? undefined
                                        : this.account.utm_params.utm_content,
                                })(
                                    <TemplatizedTextField
                                        label="utm_content"
                                        placeholder="utm content"
                                        hideEmojis={true}
                                        macroTypes={utmMacroTypes}
                                        size="small"
                                    />,
                                )}
                            </div>
                        </Well>

                        <Well
                            key={this.state.customUtmParams.length}
                            className="custom-utm-parameter-well"
                            title="UTM Custom Parameters"
                            mode="ghost"
                            actions={
                                <AntButton
                                    className="custom-utm-add-btn"
                                    size="small"
                                    onClick={this.handleCustomUtmAdd}
                                    disabled={Object.keys(this.state.customUtmParams).length >= 5}
                                >
                                    <PlusOutlined />
                                    <span>Add</span>
                                </AntButton>
                            }
                            hideFooter={true}
                        >
                            {this.state.customUtmParams.length === 0 && <div>There are no custom UTM parameters</div>}
                            {this.state.customUtmParams.map(([name, config], idx) => (
                                <div key={idx} className="adf-notif-editor-custom-utm">
                                    <Input
                                        className="custom-utm-name"
                                        size="small"
                                        type="text"
                                        placeholder="Parameter name"
                                        defaultValue={name}
                                        onKeyDown={(ev) => {
                                            // we should attempt to prevent spaces in the query param name
                                            if (ev.key === ' ' || ev.keyCode === 32) {
                                                ev.preventDefault()
                                                ev.stopPropagation()
                                            }
                                        }}
                                        onChange={(ev) =>
                                            this.handleCustomUtmChange(idx, ev.target.value, config.default)
                                        }
                                    />
                                    <TemplatizedTextField
                                        className="custom-utm-value"
                                        size="small"
                                        placeholder="Parameter value"
                                        defaultValue={config.default || undefined}
                                        hideEmojis={true}
                                        macroTypes={utmMacroTypes}
                                        onChange={(val) => this.handleCustomUtmChange(idx, name, val)}
                                    />
                                    <AntButton
                                        className="custom-utm-remove-btn"
                                        size="small"
                                        icon={<DeleteFilled />}
                                        onClick={() => this.handleCustomUtmRemove(idx)}
                                    />
                                </div>
                            ))}
                        </Well>
                    </Form>
                </Drawer>
            )
        }

        protected get account(): AccountDto {
            return this.props.account
        }

        protected getCurrentValues(): Partial<AccountDto> {
            const currentUserIsDev = this.appState.currentUser?.isInternalDevUser || false
            let values: Partial<AccountDto> = {}

            // Keywords
            values.keywords = (this.props.form.getFieldValue('keywords') || '').split(/\n/)
            values.keywords = values.keywords.map((flag) => flag.toLowerCase().trim()).filter((kw) => !!kw && kw !== '')
            if (deepEqual(values.keywords, this.account.keywords)) {
                delete values.keywords
            }

            // Lifespan
            const ttlValue = this.props.form.getFieldValue('ttlValue')
            const ttlMetric = this.props.form.getFieldValue('ttlMetric')
            const computedTtlSeconds = calculateRawTtlSecondsFromValue(ttlValue, ttlMetric)
            if (computedTtlSeconds !== this.account.default_notification_ttl_seconds) {
                values.default_notification_ttl_seconds = computedTtlSeconds
                values.display_meta = {
                    default_notification_ttl_metric: ttlMetric,
                }
            }

            // Media
            values.media_quality = this.props.form.getFieldValue('mediaQuality')
            if (values.media_quality === this.account.media_quality) {
                delete values.media_quality
            }

            // UTM Params
            const utmValues: any = {}
            const utmSource = this.props.form.getFieldValue('utmSource')
            if (utmSource) {
                utmValues.utm_source = utmSource
            }
            const utmMedium = this.props.form.getFieldValue('utmMedium')
            if (utmMedium) {
                utmValues.utm_medium = utmMedium
            }
            const utmCampaign = this.props.form.getFieldValue('utmCampaign')
            if (utmCampaign) {
                utmValues.utm_campaign = utmCampaign
            }
            const utmTerm = this.props.form.getFieldValue('utmTerm')
            if (utmTerm) {
                utmValues.utm_term = utmTerm
            }
            const utmContent = this.props.form.getFieldValue('utmContent')
            if (utmContent) {
                utmValues.utm_content = utmContent
            }

            // handle custom utm extraction
            const addedNotificationQueryParams: { [index: string]: any } = {}
            if (this.state.customUtmParams.length) {
                const customUtmParams = [...this.state.customUtmParams]
                for (const [param, config] of customUtmParams) {
                    addedNotificationQueryParams[param] = config
                }
            }

            if (!deepEqual(utmValues, this.account.utm_params)) {
                values.utm_params = utmValues
            }

            if (!deepEqual(addedNotificationQueryParams, this.account.added_notification_query_params)) {
                values.added_notification_query_params = addedNotificationQueryParams
            }

            return values
        }

        protected handleSubmit = async () => {
            try {
                await legacyValidateFields(this.props.form)
                const mergedValues = this.getCurrentValues()

                if (this.state.customUtmParams.length) {
                    const customUtmParams = [...this.state.customUtmParams]
                    for (const [param, config] of customUtmParams) {
                        if (!param?.trim() || !config.default?.trim()) {
                            throw {
                                customUtmParams: {
                                    errors: [{ message: 'Custom UTM parameter name and value are both required.' }],
                                },
                            }
                        }
                    }

                    const uniqueParamKeys = new Set(new Map(customUtmParams).keys())
                    if (uniqueParamKeys.size < customUtmParams.length) {
                        throw {
                            customUtmParams: {
                                errors: [{ message: 'Custom UTM Param names must be unique.' }],
                            },
                        }
                    }
                }

                let { ok, data } = await this.accountService.patch(this.account.id, mergedValues, true)

                if (ok) {
                    this.props.onAccountUpdated(data)
                    this.props.onClose(null)
                }
            } catch (err) {
                simpleFormErrorNotification(err)
            }
        }

        protected handleCustomUtmAdd = () => {
            this.setState(({ customUtmParams }) => {
                const params = [...customUtmParams]
                params.push([
                    '',
                    {
                        default: '',
                        property: '__explicit',
                    },
                ])

                return { customUtmParams: params }
            })
        }

        protected handleCustomUtmRemove = (idx: number) => {
            this.setState(({ customUtmParams }) => {
                const newValue = [...customUtmParams]
                newValue.splice(idx, 1)

                return { customUtmParams: newValue }
            })
        }

        protected handleCustomUtmChange = (idx: number, name: string, value: string) => {
            const customUtmParams = [...this.state.customUtmParams]

            customUtmParams[idx][0] = name.trim().replace(/\s/g, '_')
            customUtmParams[idx][1].default = value

            this.setState({ customUtmParams })
        }
    },
)

interface IAccountNotificationWell {
    loading?: boolean
    account: AccountDto
    title?: string
    onAccountUpdated: (account: Partial<AccountDto>) => void
}

interface IANWState {
    showDrawer: boolean
    drawerKey?: string
}

export class AccountNotificationWell extends React.Component<IAccountNotificationWell, IANWState> {
    public state: IANWState = {
        showDrawer: false,
    }

    protected appState: AppState

    public constructor(props: IAccountNotificationWell) {
        super(props)

        this.appState = Container.get(AppState)
    }

    public render() {
        return (
            <Well
                className="type-3 nested"
                title="Notification Settings"
                actions={
                    <Button size="small" onClick={this.handleDrawerOpen} disabled={this.props.loading}>
                        <EditOutlined />
                        <span>Edit</span>
                    </Button>
                }
                hideFooter={true}
            >
                <Skeleton loading={this.props.loading} active={true} title={false}>
                    {!this.props.loading && (
                        <>
                            {this.renderKeywordsWell()}
                            {this.renderTtlWell()}
                            {this.renderMediaWell()}
                            {this.renderUtmWell()}
                            {this.renderCustomUtmParamsWell()}

                            <AccountNotificationEditor
                                key={this.state.drawerKey}
                                visible={this.state.showDrawer}
                                account={this.props.account}
                                onClose={this.handleDrawerClose}
                                onAccountUpdated={this.props.onAccountUpdated}
                            />
                        </>
                    )}
                </Skeleton>
            </Well>
        )
    }

    protected renderKeywordsWell(): React.ReactNode {
        return (
            <Well
                className={classnames('adf-notif-editor-keywords-display')}
                title="Keywords"
                mode="ghost"
                hideFooter={true}
            >
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Default Keywords</div>
                    <div className={classnames('form-layout-row-value')}>
                        {this.props.account.keywords?.join(', ') || 'No keywords'}
                    </div>
                    <div className={classnames('form-layout-row-sub')}>
                        <InfoCircleOutlined />
                        <span>Keywords will be available on all domains associated with this account.</span>
                    </div>
                </div>
            </Well>
        )
    }

    protected renderTtlWell() {
        const rawTtlSeconds = this.account.default_notification_ttl_seconds
        const ttlMetric = this.account.display_meta?.default_notification_ttl_metric || 'days'
        const ttlValue = calculateValueFromRawTtlSeconds(rawTtlSeconds, ttlMetric)

        return (
            <Well title="Delivery" mode="ghost" hideFooter={true}>
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Default Lifespan</div>
                    <div className={classnames('form-layout-row-value')}>
                        {!!rawTtlSeconds ? `${ttlValue} ${ttlMetric.replace(/s$/, '(s)')}` : 'No default'}
                    </div>
                    <div className={classnames('form-layout-row-sub')}>
                        <InfoCircleOutlined />
                        <span>System default is 7 days.</span>
                    </div>
                </div>
            </Well>
        )
    }

    protected renderMediaWell() {
        return (
            <Well title="Media" mode="ghost" hideFooter={true}>
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Default Image Quality</div>
                    <div className={classnames('form-layout-row-value')}>{this.props.account.media_quality || 40}</div>
                    <div className={classnames('form-layout-row-sub')}>
                        <InfoCircleOutlined />
                        <span>
                            Quality can be set anywhere between 30 & 100. Values lower than 40 may significantly impact
                            images.
                        </span>
                    </div>
                </div>
            </Well>
        )
    }

    protected renderUtmWell() {
        const account = this.props.account
        let utmParams: any = {}
        if (account) {
            utmParams = account.utm_params || {}
        }

        return (
            <Well title="UTM Parameters" mode="ghost" hideFooter={true}>
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>utm_source</div>
                    <div className={classnames('form-layout-row-value')}>
                        {utmParams.utm_source && utmParams.utm_source}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>utm_medium</div>
                    <div className={classnames('form-layout-row-value')}>
                        {utmParams.utm_medium && utmParams.utm_medium}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>utm_campaign</div>
                    <div className={classnames('form-layout-row-value')}>
                        {utmParams.utm_campaign && utmParams.utm_campaign}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>utm_term</div>
                    <div className={classnames('form-layout-row-value')}>
                        {utmParams.utm_term && utmParams.utm_term}
                    </div>
                </div>

                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>utm_content</div>
                    <div className={classnames('form-layout-row-value')}>
                        {utmParams.utm_content && utmParams.utm_content}
                    </div>
                </div>
            </Well>
        )
    }

    protected renderQueryParams() {
        const queryParams = this.account.added_notification_query_params || {}
        const queryParamsStr = JSON.stringify(queryParams, null, 2)

        return (
            <Well title="Added Notification Query Params" mode="ghost" hideFooter={true}>
                <div className={classnames('form-layout-row')}>
                    <div className={classnames('form-layout-row-label')}>Params</div>
                    <div className={classnames('form-layout-row-value', 'query-params can-copy')}>
                        <CopyToClipboard onCopy={() => this.handleCopied('query params')} text={queryParamsStr}>
                            <pre>{queryParamsStr}</pre>
                        </CopyToClipboard>
                    </div>
                </div>
            </Well>
        )
    }

    protected renderCustomUtmParamsWell() {
        const queryParams = this.account.added_notification_query_params || {}

        return (
            <Well title="UTM Custom Parameters" className="custom-utm-parameter-well" mode="ghost" hideFooter={true}>
                {Object.keys(queryParams).length === 0 && <div>There are no custom UTM parameters</div>}
                {Object.entries(queryParams).map(([name, config], idx) => (
                    <div key={idx} className={classnames('form-layout-row')}>
                        <div className={classnames('form-layout-row-label')}>{name}</div>
                        <div className={classnames('form-layout-row-value')}>{config.default}</div>
                    </div>
                ))}
            </Well>
        )
    }

    protected get account(): AccountDto {
        return this.props.account
    }

    protected handleDrawerOpen = async () => {
        return this.setState({ showDrawer: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showDrawer: false })
    }

    protected handleCopied = (field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }
}
